import { formatUnits } from '@ethersproject/units';
import { Avatar, Box, Stack, Typography, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ErrorCode } from '../../../constants';
import { useAbortableTimeout, useRate } from '../../../hooks';
import { useSwap } from '../../../hooks/swap/useSwap';
import { Token, useAppSelector, useUsdStablecoins } from '../../../store';
import { Field } from '../../../types';
import { formatGweiFixed, formatUsdFixed, getErrorMessage } from '../../../utils';
import { AuxButton, MainButton, MainButtonType, SlippageButtonsGroup } from '../../buttons';
import GasPriceOptions from '../../GasPriceOptions';
import { NoLogoURI, SwitchTokensIcon } from '../../icons';
import { ConfirmSwapModalErrorMsg, RefreshRateWarningMsg } from '../../messages';
import SkeletonText from '../../SkeletonText';
import { Modal, ModalHeaderType } from '../Modal';
import SignTxModal from '../SignTxModal';
import TxSentModal from '../TxSentModal';

interface ConfirmSwapModalProps {
  isOpen: boolean;
  goBack: () => void;
  gasOptions: any;
}
interface SwapTokenBoxProps {
  field: Field;
  token: Token;
  amount: string;
  usdcPrice?: string;
}

const SwapTokenBox = ({ field, token, amount, usdcPrice }: SwapTokenBoxProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const text = field === Field.INPUT ? t('You sell') : t('You buy');
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${theme.palette.widget['border-01']}`,
        borderRadius: '16px',
        mb: '10px',
        padding: '17px 17px 21px',
      }}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="rxs12" lineHeight="14px" color="widget.text-secondary" sx={{ mb: '17px' }}>
          {text}
        </Typography>
        {usdcPrice ? (
          <Typography variant="rxs12" lineHeight="14px" color="widget.text-secondary">
            ~${usdcPrice}
          </Typography>
        ) : (
          <SkeletonText width="60px" height="14px" />
        )}
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box sx={{ display: 'flex' }}>
          <Avatar src={token.logoURI} sx={{ width: 24, height: 24, backgroundColor: 'transparent' }}>
            <NoLogoURI />
          </Avatar>
          <Typography variant="mxlg20" lineHeight="24px" color="widget.text-primary" sx={{ margin: '0 12px' }}>
            {token.symbol}
          </Typography>
        </Box>
        {amount ? (
          <Typography variant="mxlg20" lineHeight="24px" color="widget.text-primary">
            {amount}
          </Typography>
        ) : (
          <SkeletonText width="110px" height="24px" />
        )}
      </Stack>
    </Box>
  );
};

const ConfirmSwapModal = ({ isOpen, goBack, gasOptions }: ConfirmSwapModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const INPUT = useAppSelector((state) => state.tokens.tokens[state.swap.INPUT]);
  const OUTPUT = useAppSelector((state) => state.tokens.tokens[state.swap.OUTPUT]);
  const inputUsdPrice = useAppSelector((state) => state.tokens.usdPrices[INPUT?.address]);
  const outputUsdPrice = useAppSelector((state) => state.tokens.usdPrices[OUTPUT?.address]);
  const { typedValue, slippage, txFeeCalculation, estimateGasError, buildTxError } = useAppSelector(
    (state) => state.swap
  );

  const [slippageModalOpen, setSlippageModalOpen] = useState<boolean>(false);
  const [gasPriceModalOpen, setGasPriceModalOpen] = useState<boolean>(false);
  const [shouldRefresh, setShouldRefresh] = useState<boolean>(false);

  const { startTimeout, abortTimeout } = useAbortableTimeout(() => setShouldRefresh(true), 5000);
  const { defaultStablecoin } = useUsdStablecoins();

  const { executeSwap, updateTx, toTokenAmount, tx, swapLoading } = useSwap();

  const price = useRate((typedValue || '0').toString(), toTokenAmount || '0');

  const gasPrice = formatGweiFixed(
    txFeeCalculation.gasPriceSettingsMode === 'basic'
      ? txFeeCalculation.gasPriceInfo.price
      : txFeeCalculation.customGasPrice.maxFee
  );

  const error = estimateGasError ?? buildTxError;
  const ErrorMessages = {
    [ErrorCode.INTERNAL_JSON_RPC_ERROR]: t('Try to increase gas fee in settings'),
    [ErrorCode.INSUFFICIENT_OUTPUT_AMOUNT]: t('Try to increase slippage percent to make a swap'),
    [ErrorCode.DEFAULT_CONFIRM_SWAP_ERROR]: t('Try to increase slippage percent to make a swap'),
  };

  useEffect(() => {
    if (isOpen) {
      startTimeout();
      updateTx().then(() => console.log('UPDATED TX DATA', { tx, toTokenAmount, swapLoading }));
    } else {
      abortTimeout();
    }
  }, [isOpen, slippage, gasPrice]);

  useEffect(() => {
    setShouldRefresh(false);
    startTimeout();
  }, [tx, toTokenAmount]);

  useEffect(() => {
    setShouldRefresh(true);
  }, [slippage, gasPrice]);

  const handleSendTx = useCallback(() => {
    if (swapLoading) return;
    try {
      executeSwap();
    } catch (error) {
      console.error(error);
    }
  }, [executeSwap]);

  const onRefreshClick = () => {
    updateTx();
  };

  const inputUsdcPriceToDisplay =
    inputUsdPrice && defaultStablecoin && formatUsdFixed(inputUsdPrice, defaultStablecoin.decimals);
  const outputUsdcPriceToDisplay =
    outputUsdPrice && defaultStablecoin && formatUsdFixed(outputUsdPrice, defaultStablecoin.decimals);

  const shouldDisableMainButton = swapLoading || !toTokenAmount || !!estimateGasError;

  const mainButtonByType = () => {
    if (estimateGasError) {
      const errorMessage = getErrorMessage(error, ErrorMessages);
      return (
        <Stack direction="column" spacing={1}>
          <ConfirmSwapModalErrorMsg message={errorMessage} />
          <MainButton type={MainButtonType.EstimateGasError} disabled={shouldDisableMainButton} />
        </Stack>
      );
    }
    if (shouldRefresh) {
      return (
        <Stack direction="column" spacing={1}>
          <RefreshRateWarningMsg
            inputTokenSymbol={INPUT.symbol}
            outputTokenSymbol={OUTPUT.symbol}
            quoteUpdated={shouldRefresh}
          />
          <MainButton
            type={MainButtonType.Refresh}
            onClick={onRefreshClick}
            disabled={shouldDisableMainButton}
            rateExpired={shouldRefresh}
          />
        </Stack>
      );
    }
    return <MainButton type={MainButtonType.Confirm} onClick={handleSendTx} disabled={shouldDisableMainButton} />;
  };

  return isOpen ? (
    <React.Fragment>
      <Modal headerType={ModalHeaderType.Confirm} goBack={goBack} isOpen={isOpen}>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}>
          <Stack direction="column">
            <SwapTokenBox
              field={Field.INPUT}
              token={INPUT}
              amount={formatUnits(typedValue || '0x00', INPUT.decimals)}
              usdcPrice={inputUsdcPriceToDisplay}
            />
            <SwitchTokensIcon
              style={{
                position: 'absolute',
                left: '50%',
                top: '31.5%',
                transform: 'translate(-50%, -50%)',
                filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08))',
              }}
            />
            <SwapTokenBox
              field={Field.OUTPUT}
              token={OUTPUT}
              amount={
                toTokenAmount && OUTPUT.address && !swapLoading
                  ? parseFloat(formatUnits(toTokenAmount, OUTPUT.decimals)).toFixed(6)
                  : ''
              }
              usdcPrice={outputUsdcPriceToDisplay}
            />
            <Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: '9px' }}>
                <Typography variant="rxs12" lineHeight="14px" color="widget.text-secondary">
                  1 {INPUT && INPUT.symbol} <Trans>price</Trans>
                </Typography>
                <Box sx={{ display: 'flex', columnGap: '4px' }}>
                  {inputUsdcPriceToDisplay ? (
                    <Typography variant="rxs12" lineHeight="14px" color="widget.text-secondary">
                      ~${inputUsdcPriceToDisplay}
                    </Typography>
                  ) : (
                    <SkeletonText width="60px" height="14px" />
                  )}
                  {price.input ? (
                    <Typography variant="rxs12" lineHeight="14px" color="widget.text-primary">
                      {` ${price.input}  ${INPUT && (INPUT.symbol === 'ETH' ? 'Ξ' : INPUT.symbol)}`}
                    </Typography>
                  ) : (
                    <SkeletonText width="80px" height="14px" />
                  )}
                </Box>
              </Stack>

              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: '9px' }}>
                <Typography variant="rxs12" lineHeight="14px" color="widget.text-secondary">
                  1 {OUTPUT && OUTPUT.symbol} <Trans>price</Trans>
                </Typography>
                <Box sx={{ display: 'flex', columnGap: '4px' }}>
                  {outputUsdcPriceToDisplay ? (
                    <Typography variant="rxs12" lineHeight="14px" color="widget.text-secondary">
                      ~${outputUsdcPriceToDisplay}
                    </Typography>
                  ) : (
                    <SkeletonText width="50px" height="14px" />
                  )}
                  {price.output ? (
                    <Typography variant="rxs12" lineHeight="14px" color="widget.text-primary">
                      {` ${price.output}  ${OUTPUT && (OUTPUT.symbol === 'ETH' ? 'Ξ' : OUTPUT.symbol)}`}
                    </Typography>
                  ) : (
                    <SkeletonText width="70px" height="14px" />
                  )}
                </Box>
              </Stack>

              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: '9px' }}>
                <Typography variant="rxs12" lineHeight="14px" color="widget.text-secondary">
                  <Trans>Gas price</Trans>
                </Typography>
                <Box sx={{ display: 'flex', columnGap: '4px' }}>
                  <AuxButton onClick={() => setGasPriceModalOpen(true)} text={t('Edit')} />
                  <Typography variant="rxs12" lineHeight="14px" color="widget.text-primary">
                    {`${gasPrice} Gwei`}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: '9px' }}>
                <Typography variant="rxs12" lineHeight="14px" color="widget.text-secondary">
                  <Trans>Slippage</Trans>
                </Typography>
                <Box sx={{ display: 'flex', columnGap: '4px' }}>
                  <AuxButton onClick={() => setSlippageModalOpen(true)} text={t('Edit')} />
                  <Typography variant="rxs12" lineHeight="14px" color="widget.text-primary">
                    {slippage}%
                  </Typography>
                </Box>
              </Stack>
              <hr
                color={theme.palette.widget['border-01']}
                style={{
                  margin: '19px 0',
                  height: '1px',
                  borderWidth: 0,
                }}
              />
            </Stack>
          </Stack>
          {mainButtonByType()}
        </Box>
      </Modal>
      <Modal
        goBack={() => setSlippageModalOpen(false)}
        isOpen={slippageModalOpen}
        headerType={ModalHeaderType.Slippage}>
        <Box sx={{ height: '100%' }}>
          <SlippageButtonsGroup />
        </Box>
      </Modal>
      <Modal
        goBack={() => setGasPriceModalOpen(false)}
        isOpen={gasPriceModalOpen}
        headerType={ModalHeaderType.GasPrice}>
        <Box sx={{ height: '100%' }}>
          <GasPriceOptions gasOptions={gasOptions} />
        </Box>
      </Modal>
      <SignTxModal />
      <TxSentModal />
    </React.Fragment>
  ) : null;
};

export default ConfirmSwapModal;
